import "./JobDetailsComponent.scss";
import React, { useCallback, useState, useEffect, useMemo } from "react";
import { ImageConfig } from "../../constants";
import ButtonComponent from "../../components/ant-component/button-component/ButtonComponent";
import ModalComponent from "../../components/modal/ModalComponent";
import ShareLinkComponent from "../job-board/share-links/ShareLinkComponent";
import PerDiemCardComponent from "./per-diem-card/PerDiemCardComponent";
import ContractCardComponent from "./contract-card/ContractCardComponent";
import PermanentCardComponent from "./permanent-card/PermanentCardComponent";
import { useSelector } from "react-redux";
import { StateParams } from "../../store/reducers";
import JobApplicationFormComponent from "../job-board/job-application-form/JobApplicationFormComponent";
import { ArrowLeftOutlined } from '@ant-design/icons';

interface JobDetailsComponentProps {
    jobDetails: any;
    onBack?: () => void; // For mobile navigation
    isMobile?: boolean;
}

const JobDetailsComponent = (props: JobDetailsComponentProps) => {
    const { jobDetails, onBack } = props;
    const [openShareModal, setOpenShareModal] = useState<boolean>(false);
    const [linkCopied, setLinkCopied] = useState<any>({});
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);

    const { currentCustomerConfig } = useSelector((state: StateParams) => state.meta);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleOpenShareModal = useCallback(() => {
        setOpenShareModal(true);
    }, []);

    const handleApply = useCallback(() => {
        setIsModalVisible(true);
    }, []);

    const onCloseModal = useCallback(() => {
        setIsModalVisible(false);
    }, []);

    // Format salary based on job type
    const formatSalary = useCallback(() => {
        if (!jobDetails?.pay_rate || Number(jobDetails.pay_rate) === 0) {
            return jobDetails?.job_type === 'permanent'
                ? <div className='job-salary-range-title'>Salary will vary based on experience.</div>
                : <div className='job-salary-range-title'>Salary Not Disclosed</div>;
        }

        // TODO: remove this once the BE sends a bool flag for permanent jobs
        if (jobDetails?.job_type === "permanent") {
            return <div className='job-salary-range-title'>Salary will vary based on experience.</div>
        }

        const rate = Number(jobDetails.pay_rate).toFixed(2);
        switch (jobDetails?.job_type) {
            case 'permanent':
                return `$${rate}/Year`;
            case 'per_diem':
                return `$${rate}/Hr`;
            case 'contract':
                return `$${rate}/Week`;
            default:
                return 'Salary Not Disclosed';
        }
    }, [jobDetails]);

    const jobDescription = useMemo(() => {
        const shouldUseDefaultDescription = Boolean(jobDetails?.is_default_job_description);
        if (shouldUseDefaultDescription) {
            return (
                <div>
                    <div className='job-details-header'>Job Description:</div>
                    {jobDetails?.default_description ? (
                        <div
                            dangerouslySetInnerHTML={{ __html: jobDetails?.default_description || '' }}
                            className='basic-content-wrapper'
                        />
                    ) : (
                        <div className='basic-content-wrapper mrg-top-10'></div>
                    )}
                </div>
            )
        }

        return (
            <>
                <div>
                    <div className='job-details-header'>Job Description:</div>
                    {jobDetails?.job_description ? (
                        <div
                            dangerouslySetInnerHTML={{ __html: jobDetails?.job_description || '' }}
                            className='basic-content-wrapper'
                        />
                    ) : (
                        <div className='basic-content-wrapper mrg-top-10'></div>
                    )}
                </div>

                {jobDetails?.show_job_benefits && <div>
                    <div className='job-details-header'>Job Benefits:</div>
                    {jobDetails?.job_benefits ? (
                        <div
                            dangerouslySetInnerHTML={{ __html: jobDetails?.job_benefits || '' }}
                            className='basic-content-wrapper'
                        />
                    ) : (
                        <div className='basic-content-wrapper mrg-top-10'></div>
                    )}
                </div>}


            </>

        )
    }, [jobDetails])

    return (
        <>
            <ModalComponent
                open={openShareModal}
                title='Share Job'
                centered={true}
                width={isMobileView ? '100%' : 600}
                // className={isMobileView ? 'share-job-modal' : ''}
                onCancel={() => {
                    setOpenShareModal(false);
                    setLinkCopied({});
                }}
            >
                <ShareLinkComponent
                    jobDetails={jobDetails}
                    id={jobDetails?._id}
                    copied={linkCopied}
                    setCopied={setLinkCopied}
                />
            </ModalComponent>

            <ModalComponent
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                width={isMobileView ? '100%' : 600}
                title='Apply For This Job'
                destroyOnClose={true}
                footer={null}
            >
                <JobApplicationFormComponent
                    jobId={jobDetails?._id}
                    onCloseModal={onCloseModal}
                />
            </ModalComponent>

            <div className='job-details-component'>
                <div className='job-top-section'>
                    {isMobileView && onBack && (
                        <div className='mobile-job-details-header'>
                            <div className='back-button' onClick={onBack}>
                                <ArrowLeftOutlined />
                                <span>Back to Jobs</span>
                            </div>
                        </div>
                    )}

                    <div className='job-details-section-one'>
                        <div className='job-details-section-header'>
                            <div className='job-details-image-wrapper'>
                                <img
                                    src={currentCustomerConfig?.customer_logo?.file_url  || ImageConfig.FacilityImage}
                                    alt={"logo"}
                                    className='job-details-image'
                                />
                            </div>
                            <div className='job-details-content'>
                                <div className='job-details-title'>{jobDetails?.job_title}</div>
                                <div className='d-flex align-items-center flex-wrap'>
                                    {jobDetails?.posting_status?.show_facility && jobDetails?.facility_details?.name && (
                                        <div className='job-details-address mrg-top-10 mrg-right-10'>
                                            {jobDetails?.facility_details?.name || ''}
                                        </div>
                                    )}
                                    <div className='job-details-address mrg-top-10'>
                                        {[
                                            jobDetails?.address?.city,
                                            jobDetails?.address?.state,
                                            jobDetails?.address?.zip_code
                                        ].filter(Boolean).join(", ")}
                                    </div>
                                </div>
                                {jobDetails?.customer_name?.trim() ? (
                                    <div className='job-details-address mrg-top-10'>
                                        Company: {jobDetails.customer_name}
                                    </div>
                                ) : null}
                                {jobDetails?._id ? (
                                    <div className='job-details-address mrg-top-10'>
                                        Job ID: {jobDetails._id.slice(-7)}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                        <div className='job-details-rate-wrapper'>
                            <div className='job-salary-range'>
                                {formatSalary()}
                            </div>
                            <div className='job-details-header-actions'>
                                <ButtonComponent
                                    color='primary'
                                    type='button'
                                    onClick={handleOpenShareModal}
                                    size={isMobileView ? 'middle' : 'large'}
                                >
                                    Share
                                </ButtonComponent>
                                <ButtonComponent
                                    color='primary'
                                    variant='solid'
                                    type='button'
                                    size={isMobileView ? 'middle' : 'large'}
                                    onClick={handleApply}
                                    className='job-details-apply-btn'
                                >
                                    Apply
                                </ButtonComponent>
                            </div>
                        </div>
                    </div>

                    <div className='job-details-section-two'>
                        <div className='job-details-info-wrapper'>
                            {jobDetails?.job_type === 'per_diem' ? (
                                <PerDiemCardComponent jobDetails={jobDetails} />
                            ) : jobDetails?.job_type === 'contract' ? (
                                <ContractCardComponent jobDetails={jobDetails} />
                            ) : (
                                <PermanentCardComponent jobDetails={jobDetails} />
                            )}
                        </div>

                        {jobDetails?.posting_status?.show_facility && jobDetails?.facility_details?.about && (
                            <div className='job-details-facility-wrapper'>
                                <div className='job-details-facility-header'>About Facility</div>
                                <div className='basic-content-wrapper'>
                                    {jobDetails?.facility_details?.about || ''}
                                </div>
                            </div>
                        )}

                        {jobDescription}

                    </div>
                </div>
            </div>
        </>
    );
};

export default JobDetailsComponent;
